import type { CommunityEntry, PublicConfigSummary } from "@cfg/api-contract";
import type { CatalogGroup } from "./catalog.ts";

export type CommunitySort = "newest" | "downloads" | "confirmed" | "likes" | "name";
export const COMMUNITY_SORTS: CommunitySort[] = ["newest", "downloads", "confirmed", "likes", "name"];

export interface CommunityText {
  /** Display title of an entry's design (family siblings differ). */
  titleOf?: (designId: string) => string;
  /** Display name of an entry: its parameter summary (generated from its params where the spec is known). */
  nameOf?: (entry: CommunityEntry) => string;
}

/**
 * Entries matching every word of `query` in their name, taggers, design title,
 * fit refs or fit names. "42115" finds "42115-1"; case-insensitive.
 */
export function filterCommunity(items: CommunityEntry[], query: string, text: CommunityText = {}): CommunityEntry[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return items;
  const titleOf = text.titleOf ?? ((id: string) => id);
  const nameOf = text.nameOf ?? ((e: CommunityEntry) => e.id);
  return items.filter((e) => {
    const hay = [nameOf(e), ...e.taggers, titleOf(e.designId), ...e.fits.flatMap((f) => [f.ref, f.name ?? ""])].join(" ").toLowerCase();
    return terms.every((t) => hay.includes(t));
  });
}

/** A sorted copy. Ties fall back to newest first, the order the API lists them in. */
export function sortCommunity(items: CommunityEntry[], sort: CommunitySort, locale?: string, nameOf: (e: CommunityEntry) => string = (e) => e.id): CommunityEntry[] {
  const newest = (a: CommunityEntry, b: CommunityEntry) => b.updatedAt.localeCompare(a.updatedAt) || b.id.localeCompare(a.id);
  const by: Record<CommunitySort, (a: CommunityEntry, b: CommunityEntry) => number> = {
    newest,
    downloads: (a, b) => b.downloadCount - a.downloadCount || newest(a, b),
    // People who printed it, for any of its sets.
    confirmed: (a, b) => b.printerCount - a.printerCount || newest(a, b),
    likes: (a, b) => b.likeCount - a.likeCount || newest(a, b),
    name: (a, b) => nameOf(a).localeCompare(nameOf(b), locale, { sensitivity: "base" }) || newest(a, b),
  };
  return [...items].sort(by[sort]);
}

/**
 * Catalog cards (families grouped) that have entries tagged for `ref`: how
 * many entries and how many prints registered for that set, a family's
 * members counted together. Most printed first, then most entries, then
 * catalog order.
 */
export function designsForFit(groups: CatalogGroup[], entries: PublicConfigSummary[], ref: string): { group: CatalogGroup; count: number; prints: number }[] {
  const tally = new Map<string, { count: number; prints: number }>();
  for (const e of entries) {
    const t = tally.get(e.designId) ?? { count: 0, prints: 0 };
    t.count++;
    t.prints += e.fits.find((f) => f.ref === ref)?.confirmCount ?? 0;
    tally.set(e.designId, t);
  }
  const sum = (g: CatalogGroup, k: "count" | "prints") => g.members.reduce((n, m) => n + (tally.get(m.id)?.[k] ?? 0), 0);
  return groups
    .map((group, i) => ({ group, count: sum(group, "count"), prints: sum(group, "prints"), i }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.prints - a.prints || b.count - a.count || a.i - b.i)
    .map(({ group, count, prints }) => ({ group, count, prints }));
}

/** A set the community tagged entries for, as "Start from a set" lists it. */
export interface CommunitySet {
  ref: string;
  name?: string;
  /** Entries tagged for it. */
  entries: number;
  /** Prints registered for it, across those entries. */
  prints: number;
}

/** Every set tagged on `items`, most printed first, then most entries, then by ref. */
export function communitySets(items: CommunityEntry[]): CommunitySet[] {
  const sets = new Map<string, CommunitySet>();
  for (const e of items) {
    for (const f of e.fits) {
      const s = sets.get(f.ref) ?? { ref: f.ref, entries: 0, prints: 0 };
      s.entries++;
      s.prints += f.confirmCount;
      if (!s.name && f.name) s.name = f.name;
      sets.set(f.ref, s);
    }
  }
  return [...sets.values()].sort((a, b) => b.prints - a.prints || b.entries - a.entries || a.ref.localeCompare(b.ref));
}

/**
 * The entry to start from for set `ref`: the most prints for that set, then most people who
 * printed it at all, then most downloads, then newest. Prefers entries made for `designId`
 * over family siblings when they tie on prints.
 */
export function bestEntryForSet(items: CommunityEntry[], ref: string, designId?: string): CommunityEntry | undefined {
  const prints = (e: CommunityEntry) => e.fits.find((f) => f.ref === ref)?.confirmCount ?? 0;
  const own = (e: CommunityEntry) => (designId && e.designId === designId ? 1 : 0);
  return items
    .filter((e) => e.fits.some((f) => f.ref === ref))
    .sort((a, b) => prints(b) - prints(a) || own(b) - own(a) || b.printerCount - a.printerCount || b.downloadCount - a.downloadCount || b.updatedAt.localeCompare(a.updatedAt))[0];
}

/** The set typed: an exact ref, else the only ref or name containing it ("42115" finds "42115-1"). */
export function matchSet(sets: CommunitySet[], input: string): CommunitySet | undefined {
  const q = input.trim().toLowerCase();
  if (!q) return undefined;
  const exact = sets.find((s) => s.ref.toLowerCase() === q);
  if (exact) return exact;
  const hits = sets.filter((s) => s.ref.toLowerCase().startsWith(q) || (s.name ?? "").toLowerCase().includes(q));
  return hits.length === 1 ? hits[0] : undefined;
}
